import React, { useEffect, useState } from 'react';
import AdminShell from '../components/AdminShell';
import { api, fmtZAR, formatApiError } from '../lib/api';

const AdminPayments = () => {
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [households, setHouseholds] = useState([]);
  const [billingMonth, setBillingMonth] = useState('2027-01');
  const [status, setStatus] = useState('');

  const [newPayment, setNewPayment] = useState({ household_id: '', amount_cents: '', received_at: new Date().toISOString().slice(0,10), reference: '', notes: '' });
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const q = new URLSearchParams();
    if (status) q.append('status', status);
    const [i, p, h] = await Promise.all([
      api.get(`/admin/invoices?${q.toString()}`),
      api.get('/admin/payments'),
      api.get('/admin/households'),
    ]);
    setInvoices(i.data.items || []);
    setPayments(p.data.items || []);
    setHouseholds(h.data.items || []);
  };
  useEffect(() => { load(); }, [status]);

  const generate = async () => {
    setErr(''); setBusy(true);
    try { await api.post(`/admin/invoices/generate?billing_month=${encodeURIComponent(billingMonth)}`); await load(); }
    catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const recordPayment = async (e) => {
    e.preventDefault(); setErr(''); setBusy(true);
    try {
      await api.post('/admin/payments', {
        household_id: newPayment.household_id,
        amount_cents: parseInt(newPayment.amount_cents, 10) * 100,
        received_at: new Date(newPayment.received_at).toISOString(),
        reference: newPayment.reference || null,
        allocations: [],
        notes: newPayment.notes || null,
      });
      setNewPayment({ household_id: '', amount_cents: '', received_at: new Date().toISOString().slice(0,10), reference: '', notes: '' });
      await load();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <AdminShell title="Payments & Invoices">
      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <div className="bg-white rounded-2xl border border-black/5 p-5" data-testid="generate-invoices-card">
            <p className="text-xs font-mono tracking-wider text-[#D4A017] mb-2">GENERATE MONTHLY INVOICES</p>
            <div className="flex gap-3 items-center">
              <input className="apc-input !w-auto" value={billingMonth} onChange={(e) => setBillingMonth(e.target.value)} placeholder="2027-01" data-testid="billing-month-input"/>
              <button onClick={generate} disabled={busy} className="btn-pill btn-primary" data-testid="generate-invoices-btn">Generate</button>
            </div>
            <p className="text-xs text-[#4B5563] mt-2">Creates one invoice per household with line items for every active subject subscription.</p>
          </div>

          <div className="bg-white rounded-2xl border border-black/5 overflow-x-auto" data-testid="invoices-table">
            <div className="flex items-center justify-between p-4 border-b border-black/5">
              <p className="font-semibold text-[#1B2A41]">Invoices</p>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className="apc-input !w-auto !py-1.5" data-testid="invoice-status-filter">
                <option value="">All</option><option value="pending">Pending</option><option value="partial">Partial</option><option value="paid">Paid</option><option value="overdue">Overdue</option>
              </select>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-[#F3F5F2] text-left"><tr>
                <th className="px-4 py-3 font-semibold">Month</th><th className="px-4 py-3 font-semibold">Household</th><th className="px-4 py-3 font-semibold">Billed</th><th className="px-4 py-3 font-semibold">Paid</th><th className="px-4 py-3 font-semibold">Status</th>
              </tr></thead>
              <tbody>
                {invoices.map((i) => (
                  <tr key={i.id} className="border-t border-black/5" data-testid={`invoice-row-${i.id}`}>
                    <td className="px-4 py-3">{i.billing_month}</td>
                    <td className="px-4 py-3 text-xs text-[#4B5563]">{i.household_id.slice(0,8)}…</td>
                    <td className="px-4 py-3">{fmtZAR(i.amount_cents)}</td>
                    <td className="px-4 py-3">{fmtZAR(i.paid_cents || 0)}</td>
                    <td className="px-4 py-3"><span className="text-xs font-semibold px-2 py-1 rounded-full bg-[#F3F5F2]">{i.status}</span></td>
                  </tr>
                ))}
                {!invoices.length && <tr><td colSpan={5} className="px-4 py-8 text-center text-[#4B5563]">No invoices yet. Generate for a billing month above.</td></tr>}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-2xl border border-black/5 overflow-x-auto" data-testid="payments-table">
            <p className="font-semibold text-[#1B2A41] p-4 border-b border-black/5">Verified payments</p>
            <table className="w-full text-sm">
              <thead className="bg-[#F3F5F2] text-left"><tr>
                <th className="px-4 py-3 font-semibold">Received</th><th className="px-4 py-3 font-semibold">Household</th><th className="px-4 py-3 font-semibold">Amount</th><th className="px-4 py-3 font-semibold">Reference</th>
              </tr></thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id} className="border-t border-black/5"><td className="px-4 py-3">{p.received_at?.slice(0,10)}</td><td className="px-4 py-3 text-xs text-[#4B5563]">{p.household_id.slice(0,8)}…</td><td className="px-4 py-3">{fmtZAR(p.amount_cents)}</td><td className="px-4 py-3 text-xs">{p.reference || '—'}</td></tr>
                ))}
                {!payments.length && <tr><td colSpan={4} className="px-4 py-8 text-center text-[#4B5563]">No payments recorded.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        <aside>
          <form onSubmit={recordPayment} className="bg-white rounded-2xl border border-black/5 p-5" data-testid="record-payment-form">
            <p className="text-xs font-mono tracking-wider text-[#D4A017] mb-3">RECORD VERIFIED PAYMENT</p>
            <label className="block mb-3"><span className="text-xs font-semibold text-[#4B5563]">Household</span>
              <select required className="apc-input mt-1" value={newPayment.household_id} onChange={(e) => setNewPayment({...newPayment, household_id: e.target.value})} data-testid="payment-household">
                <option value="">Choose household…</option>
                {households.map((h) => <option key={h.id} value={h.id}>{h.parent_name} ({h.whatsapp})</option>)}
              </select>
            </label>
            <label className="block mb-3"><span className="text-xs font-semibold text-[#4B5563]">Amount (R)</span>
              <input required type="number" step="0.01" className="apc-input mt-1" value={newPayment.amount_cents} onChange={(e) => setNewPayment({...newPayment, amount_cents: e.target.value})} data-testid="payment-amount"/>
            </label>
            <label className="block mb-3"><span className="text-xs font-semibold text-[#4B5563]">Received on</span>
              <input required type="date" className="apc-input mt-1" value={newPayment.received_at} onChange={(e) => setNewPayment({...newPayment, received_at: e.target.value})}/>
            </label>
            <label className="block mb-3"><span className="text-xs font-semibold text-[#4B5563]">Bank reference</span>
              <input className="apc-input mt-1" value={newPayment.reference} onChange={(e) => setNewPayment({...newPayment, reference: e.target.value})} placeholder="APC-2027-xxxx"/>
            </label>
            <label className="block mb-3"><span className="text-xs font-semibold text-[#4B5563]">Notes</span>
              <input className="apc-input mt-1" value={newPayment.notes} onChange={(e) => setNewPayment({...newPayment, notes: e.target.value})}/>
            </label>
            {err && <p className="text-xs text-red-600 mb-3">{err}</p>}
            <button type="submit" disabled={busy} className="btn-pill btn-primary w-full justify-center" data-testid="record-payment-submit">{busy ? 'Saving…' : 'Record payment'}</button>
            <p className="text-xs text-[#4B5563] mt-3">You confirm you have verified this amount against the actual bank transaction.</p>
          </form>
        </aside>
      </div>
      <style>{`.apc-input { width:100%; padding:0.6rem 0.9rem; border-radius:10px; border:1px solid rgba(27,42,65,0.15); background:#fff; font-size:0.9rem; }`}</style>
    </AdminShell>
  );
};

export default AdminPayments;
